/**
 * Isolierter Store: Seminar-Abendessen Config + Registrierungen (Firestore).
 */
import { create } from 'zustand';
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  SEMINAR_DINNER_CONFIGS_COLLECTION,
  SEMINAR_DINNER_REGISTRATIONS_COLLECTION,
  type SeminarDinnerConfig,
  type SeminarDinnerDay,
  type SeminarDinnerRegistration,
} from '../types/seminarDinner';

type HeadcountByTag = Record<string, number>;

type SeminarDinnerState = {
  seminarId: string | null;
  config: SeminarDinnerConfig | null;
  registrations: SeminarDinnerRegistration[];
  isLoading: boolean;
  error: string | null;
  subscribe: (seminarId: string) => void;
  unsubscribe: () => void;
  /** Aggregat: Personenanzahl je tagId */
  getHeadcountByTag: () => HeadcountByTag;
  saveConfig: (config: SeminarDinnerConfig) => Promise<void>;
  updateDayLocation: (
    tagId: string,
    patch: Pick<SeminarDinnerDay, 'restaurantName' | 'adresseTreffpunkt' | 'uhrzeit'>
  ) => Promise<void>;
  /** Abende manuell setzen (Datum/Label unabhängig vom Seminartag-Plan) */
  setTage: (tage: SeminarDinnerDay[]) => Promise<void>;
  addRegistration: (
    data: Omit<SeminarDinnerRegistration, 'id' | 'angemeldetAm'> & { angemeldetAm?: string }
  ) => Promise<string>;
  updateRegistration: (
    id: string,
    patch: Partial<
      Pick<
        SeminarDinnerRegistration,
        'name' | 'email' | 'anmeldungen' | 'bemerkung' | 'locationMailSentAt'
      >
    >
  ) => Promise<void>;
  deleteRegistration: (id: string) => Promise<void>;
  /** Phase-1: markiert Empfänger-E-Mails als eingeladen */
  markInvitationMailsSent: (emails: string[]) => Promise<void>;
  /** Phase-2: markiert Registrierungen als mit Location-Mail versorgt */
  markLocationMailsSent: (registrationIds: string[]) => Promise<void>;
};

let unsubConfig: Unsubscribe | null = null;
let unsubRegs: Unsubscribe | null = null;
let activeSeminarId: string | null = null;

const mapRegistration = (id: string, data: Record<string, unknown>): SeminarDinnerRegistration => {
  const rawAnm = (data.anmeldungen && typeof data.anmeldungen === 'object'
    ? data.anmeldungen
    : {}) as Record<string, unknown>;
  const anmeldungen: Record<string, number> = {};
  Object.keys(rawAnm).forEach((k) => {
    const n = Number(rawAnm[k]);
    anmeldungen[k] = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  });

  return {
    id,
    seminarId: String(data.seminarId || ''),
    name: String(data.name || ''),
    email: String(data.email || ''),
    anmeldungen,
    bemerkung: data.bemerkung != null ? String(data.bemerkung) : undefined,
    angemeldetAm: String(data.angemeldetAm || data.createdAtIso || new Date().toISOString()),
    isManualEntry: data.isManualEntry === true,
    locationMailSentAt:
      data.locationMailSentAt != null ? String(data.locationMailSentAt) : undefined,
  };
};

const mapInvitationLog = (raw: unknown): Record<string, string> => {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([email, ts]) => {
    const key = String(email || '').trim().toLowerCase();
    if (!key) return;
    out[key] = String(ts || '');
  });
  return out;
};

const mapConfig = (seminarId: string, data: Record<string, unknown>): SeminarDinnerConfig => {
  const rawTage = Array.isArray(data.tage) ? data.tage : [];
  const tage: SeminarDinnerDay[] = rawTage.map((item: any, index: number) => ({
    tagId: String(item?.tagId || item?.id || `tag_${index}`),
    label: String(item?.label || `Abend ${index + 1}`),
    datum: String(item?.datum || item?.date || ''),
    restaurantName: item?.restaurantName != null ? String(item.restaurantName) : undefined,
    adresseTreffpunkt: item?.adresseTreffpunkt != null ? String(item.adresseTreffpunkt) : undefined,
    uhrzeit: item?.uhrzeit != null ? String(item.uhrzeit) : undefined,
  }));

  return {
    seminarId,
    titel: String(data.titel || data.title || ''),
    tage,
    invitationMailSentAtByEmail: mapInvitationLog(data.invitationMailSentAtByEmail),
  };
};

export const useSeminarDinnerStore = create<SeminarDinnerState>((set, get) => ({
  seminarId: null,
  config: null,
  registrations: [],
  isLoading: false,
  error: null,

  subscribe: (seminarId: string) => {
    const id = String(seminarId || '').trim();
    if (!id) return;
    if (activeSeminarId === id && (unsubConfig || unsubRegs)) return;

    get().unsubscribe();
    activeSeminarId = id;
    set({ seminarId: id, isLoading: true, error: null, config: null, registrations: [] });

    unsubConfig = onSnapshot(
      doc(db, SEMINAR_DINNER_CONFIGS_COLLECTION, id),
      (snap) => {
        if (!snap.exists()) {
          set({ config: null, isLoading: false });
          return;
        }
        set({ config: mapConfig(id, snap.data() as Record<string, unknown>), isLoading: false });
      },
      (err) => {
        console.error('[seminarDinnerStore] config:', err);
        set({ error: err.message || 'Config-Laden fehlgeschlagen', isLoading: false });
      }
    );

    unsubRegs = onSnapshot(
      query(
        collection(db, SEMINAR_DINNER_REGISTRATIONS_COLLECTION),
        where('seminarId', '==', id)
      ),
      (snap) => {
        const list = snap.docs.map((d) => mapRegistration(d.id, d.data() as Record<string, unknown>));
        list.sort((a, b) => String(a.angemeldetAm).localeCompare(String(b.angemeldetAm)));
        set({ registrations: list, isLoading: false });
      },
      (err) => {
        console.error('[seminarDinnerStore] registrations:', err);
        set({ error: err.message || 'Anmeldungen-Laden fehlgeschlagen', isLoading: false });
      }
    );
  },

  unsubscribe: () => {
    if (unsubConfig) {
      unsubConfig();
      unsubConfig = null;
    }
    if (unsubRegs) {
      unsubRegs();
      unsubRegs = null;
    }
    activeSeminarId = null;
  },

  getHeadcountByTag: () => {
    const totals: HeadcountByTag = {};
    const { config, registrations } = get();
    (config?.tage || []).forEach((t) => {
      totals[t.tagId] = 0;
    });
    registrations.forEach((r) => {
      Object.entries(r.anmeldungen || {}).forEach(([tagId, n]) => {
        const v = Number(n) || 0;
        if (v <= 0) return;
        totals[tagId] = (totals[tagId] || 0) + v;
      });
    });
    return totals;
  },

  saveConfig: async (config) => {
    const seminarId = String(config.seminarId || '').trim();
    if (!seminarId) throw new Error('seminarId fehlt');
    await setDoc(
      doc(db, SEMINAR_DINNER_CONFIGS_COLLECTION, seminarId),
      {
        seminarId,
        titel: config.titel || '',
        tage: config.tage || [],
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  },

  updateDayLocation: async (tagId, patch) => {
    const { config } = get();
    if (!config?.seminarId) throw new Error('Keine Config geladen');
    const tage = (config.tage || []).map((t) =>
      t.tagId === tagId
        ? {
            ...t,
            restaurantName: patch.restaurantName,
            adresseTreffpunkt: patch.adresseTreffpunkt,
            uhrzeit: patch.uhrzeit,
          }
        : t
    );
    await get().saveConfig({ ...config, tage });
  },

  setTage: async (tage) => {
    const { config, seminarId } = get();
    const id = seminarId || config?.seminarId;
    if (!id) throw new Error('Keine Config geladen');
    await get().saveConfig({
      seminarId: id,
      titel: config?.titel || '',
      tage: tage || [],
      invitationMailSentAtByEmail: config?.invitationMailSentAtByEmail,
    });
  },

  addRegistration: async (data) => {
    const seminarId = String(data.seminarId || get().seminarId || '').trim();
    if (!seminarId) throw new Error('seminarId fehlt');
    const angemeldetAm = data.angemeldetAm || new Date().toISOString();
    const payload = {
      seminarId,
      name: String(data.name || '').trim(),
      email: String(data.email || '').trim().toLowerCase(),
      anmeldungen: data.anmeldungen || {},
      bemerkung: data.bemerkung != null ? String(data.bemerkung) : '',
      angemeldetAm,
      isManualEntry: data.isManualEntry === true,
      createdAt: serverTimestamp(),
    };
    const ref = await addDoc(collection(db, SEMINAR_DINNER_REGISTRATIONS_COLLECTION), payload);
    return ref.id;
  },

  updateRegistration: async (id, patch) => {
    const clean: Record<string, unknown> = { updatedAt: serverTimestamp() };
    if (patch.name != null) clean.name = String(patch.name).trim();
    if (patch.email != null) clean.email = String(patch.email).trim().toLowerCase();
    if (patch.anmeldungen != null) clean.anmeldungen = patch.anmeldungen;
    if (patch.bemerkung != null) clean.bemerkung = String(patch.bemerkung);
    if (patch.locationMailSentAt != null) clean.locationMailSentAt = String(patch.locationMailSentAt);
    await updateDoc(doc(db, SEMINAR_DINNER_REGISTRATIONS_COLLECTION, id), clean);
  },

  deleteRegistration: async (id) => {
    await deleteDoc(doc(db, SEMINAR_DINNER_REGISTRATIONS_COLLECTION, id));
  },

  markInvitationMailsSent: async (emails) => {
    const seminarId = get().seminarId || get().config?.seminarId;
    if (!seminarId) throw new Error('seminarId fehlt');
    const now = new Date().toISOString();
    const prev = { ...(get().config?.invitationMailSentAtByEmail || {}) };
    emails.forEach((raw) => {
      const key = String(raw || '').trim().toLowerCase();
      if (key.includes('@')) prev[key] = now;
    });
    await setDoc(
      doc(db, SEMINAR_DINNER_CONFIGS_COLLECTION, seminarId),
      { invitationMailSentAtByEmail: prev, updatedAt: serverTimestamp() },
      { merge: true }
    );
  },

  markLocationMailsSent: async (registrationIds) => {
    const now = new Date().toISOString();
    const ids = (registrationIds || []).filter(Boolean);
    await Promise.all(
      ids.map((id) =>
        updateDoc(doc(db, SEMINAR_DINNER_REGISTRATIONS_COLLECTION, id), {
          locationMailSentAt: now,
          updatedAt: serverTimestamp(),
        })
      )
    );
  },
}));

/** Neuer leerer Abend (manuell pflegbar). */
export function createEmptyDinnerDay(partial?: Partial<SeminarDinnerDay>): SeminarDinnerDay {
  return {
    tagId: `abend_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    label: '',
    datum: '',
    ...partial,
  };
}

/** Label-Vorschlag aus ISO-Datum, z.B. "Samstagabend" — Admin kann überschreiben. */
export function suggestDinnerLabelFromDate(isoDate: string): string {
  const iso = String(isoDate || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const dt = new Date(`${iso}T12:00:00`);
  if (isNaN(dt.getTime())) return '';
  const wd = dt.toLocaleDateString('de-DE', { weekday: 'long' });
  return wd ? `${wd}abend` : '';
}
